import analyticsModule from './analytics-module';
import { requestIdleCallback } from '@scale/frontend-lib/ric';
import { EventMapping } from '@scale/shared/types/analytics/EventMapping';
import { MatchesSchema } from '@scale/shared/types/analytics/MatchesSchema';
import { IS_FEDERAL, IS_PROD } from '../constants';
import { UTMParameters } from '@scale/shared-types';

type AnyObject = Record<string, any>;

interface IAnalytics {
  setAnonymousId: <T extends {} = {}>(properties?: T) => void;
  identify: <T extends {} = {}>(userId: string, traits: T, options?: AnyObject) => void;
  page: <T extends {} = {}>(
    category?: string,
    name?: string,
    properties?: T,
    options?: AnyObject,
  ) => void;

  // TODO: at some point we should make `properties` a required param,
  // so we can be sure that context is always provided for every event
  // See https://scale-api.quip.com/6cL0Ab9UNMFt for schema definition details.
  track: <E extends keyof EventMapping, T extends MatchesSchema<EventMapping[E], T>>(
    event: E,
    properties?: T,
    options?: AnyObject,
  ) => void;
}

export interface IAnalyticsWithTrackTimesAndContext extends IAnalytics {
  trackStartTime: (name: string, params?: AnyObject) => void;
  trackEndTime: (name: string, params?: AnyObject) => void;
  flushTrackTimes: (params: AnyObject) => void;
  setContext: <T extends {}>(properties: T) => void;
  clearContext: () => void;
}

declare global {
  interface Window {
    analytics: IAnalytics;
  }
}

let analytics: IAnalytics;

export default function createAnalyticsWrapper(
  writeKey: string,
): IAnalyticsWithTrackTimesAndContext {
  const isSegmentDisabled = IS_FEDERAL;

  const times: Record<
    string,
    {
      name: string;
      params: any;
      time: number;
    }
  > = {};

  let contextProperties = {};

  const getPropertiesWithSessionInfo = async <T extends {} = {}>(properties: T) => {
    let enhancedProperties = {
      ...properties,
    };
    try {
      const { getClientSession } = await import('@scale/shared/outlier/sessions/index');
      const sessionId = getClientSession();
      if (sessionId) {
        enhancedProperties = {
          ...enhancedProperties,
          sessionId,
        };
      }
    } catch (error) {
      console.debug('Failed to get sessionId for analytics tracking');
    }

    try {
      const userStore = await import('@scale/shared/outlier/userStore');
      const user = userStore.default.getUser();
      const isInternalUser = user?.isCorporateUser ?? false;

      enhancedProperties = {
        ...enhancedProperties,
        isInternal: isInternalUser,
      };
    } catch (error) {
      enhancedProperties = {
        ...enhancedProperties,
        isInternal: false,
      };
    }
    return enhancedProperties;
  };

  const analyticsInstance: IAnalyticsWithTrackTimesAndContext = {
    setAnonymousId<T extends {} = {}>(properties?: T) {
      if (
        properties &&
        UTMParameters.ANONYMOUS_ID in properties &&
        properties[UTMParameters.ANONYMOUS_ID]
      ) {
        // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#override-the-anonymous-id-from-the-segment-snippet
        getAnalytics().setAnonymousId(properties[UTMParameters.ANONYMOUS_ID]);
      }
    },
    identify(...args) {
      debug('identify', ...args);
      if (isSegmentDisabled) {
        return;
      }

      return doAsync(() => {
        const traits = args[1];
        analyticsInstance.setAnonymousId(traits);
        getAnalytics().identify(...args);
      });
    },
    page(...args) {
      debug('page', ...args);
      if (isSegmentDisabled) {
        return;
      }

      return doAsync(async () => {
        if (args[0] && args[1]) {
          const [category, name, properties] = [args[0], args[1], args[2] ?? {}];
          const enhancedProperties = await getPropertiesWithSessionInfo(properties);
          analyticsInstance.setAnonymousId(enhancedProperties);
          getAnalytics().page(category, name, enhancedProperties);
        } else {
          analyticsInstance.setAnonymousId(args[2]);
          getAnalytics().page(...args);
        }
      });
    },
    /**
     * IMPORTANT:
     * DO NOT include any data that could contain PHI
     *  (no task attachment contents, no responses, etc.)
     */
    track<E extends keyof EventMapping, T extends MatchesSchema<EventMapping[E], T>>(
      event: E,
      properties?: T,
      options?: AnyObject,
    ) {
      if (isSegmentDisabled) {
        return;
      }

      return doAsync(async () => {
        analyticsInstance.setAnonymousId(properties);

        let enhancedProperties = {
          ...properties,
          ...contextProperties,
        };

        enhancedProperties = await getPropertiesWithSessionInfo(enhancedProperties);

        debug('track', event, enhancedProperties, options, contextProperties);
        getAnalytics().track(event, enhancedProperties as T, options);
      });
    },
    trackStartTime(name: string, params: any) {
      if (name in times) {
        console.debug(`The event ${name} is already started.`);
      }

      times[name] = {
        name,
        params,
        time: performance.now(),
      };
    },
    trackEndTime(name: string, params: any) {
      if (!(name in times)) {
        console.debug(`The event ${name} was not started.`);
        return;
      }
      const event = times[name];
      const timeSpent = performance.now() - event.time;
      if (isSegmentDisabled) {
        return;
      }

      analyticsInstance.track(name, {
        ...event.params,
        ...params,
        timeSpent,
      });

      delete times[name];
    },
    flushTrackTimes(params) {
      for (const name of Object.keys(times)) {
        analyticsInstance.trackEndTime(name, params);
      }
    },
    setContext(properties) {
      contextProperties = {
        ...contextProperties,
        ...properties,
      };
    },
    clearContext() {
      contextProperties = {};
    },
  };

  return analyticsInstance;

  function debug(key: string, ...args: unknown[]) {
    if (!IS_PROD) {
      console.debug('Segment', key, ...args);
    }
  }

  function doAsync(fn: (deadline: IdleDeadline) => void) {
    return requestIdleCallback(fn, { timeout: 500 }); // 0.5s
  }

  function getAnalytics() {
    if (IS_FEDERAL) {
      throw new Error('Disallowed');
    }
    analytics =
      analytics ||
      analyticsModule({
        writeKey,
        skipPageCall: true,
      });
    return analytics;
  }
}
